import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";

import { Observable, Subject, Subscription, combineLatest } from "rxjs";
import { catchError } from 'rxjs/operators';

import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesSearchResultsWithCriteria } from '../entities/acces-search-results-with-criteria';
import { AccesIncomplet } from '../entities/acces-incomplet.entity';
import { AccesSearchCriteria } from '../entities/acces-search-criteria';
import { AccesActionRequest } from '../entities/acces-action-request';
import { AccesTLR } from '../entities/acces-tlr.entity';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { AccesService } from '../services/acces.service';
import { UserService } from '../../../humain/user/services/user.service';

import { AccesEtat } from '../../../../app.constants';

import { CoactiviteEolienCheckCommand } from './coactivite-eolien-checker.component';
import { CoactiviteSolaireCheckCommand } from './coactivite-solaire-checker.component';
import { CoactiviteDecision } from '../entities/coactivite-decision.constants';

@Component({ template: ''})
export abstract class GestionAccesGenericComponentBase<T> implements OnInit, OnDestroy {
	accesEtat: typeof AccesEtat = AccesEtat;
	modificationTarifNegatifCallSubject$ = new Subject<number[]>();
	
	title: string= '';
	
	searchCriteria: AccesSearchCriteria = new AccesSearchCriteria();

	reloadAccesPlanifieCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesDemandesCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesEnCoursCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesDemandeFermetureCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesTerminesCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesRefusesCommands = new Subject<AccesSearchResultsWithCriteria>();
	reloadAccesEnErreurCommands = new Subject<AccesSearchResultsWithCriteria>();
	
	checkCoactiviteEolienCommands = new Subject<CoactiviteEolienCheckCommand>();
	checkCoactiviteSolaireCommands = new Subject<CoactiviteSolaireCheckCommand>();
	
	user!: UserTLR;
	displayAccesDetailsSubject = new Subject<number>();
	requestAccesActionSubject = new Subject<number>();
	displayAccesFermetureCommands = new Subject<number>();
	displayAssociationArretsCommands = new Subject<number[]>();
	displayRefusAccesCommands = new Subject<number[]>();
	displayControleSecuriteCommands = new Subject<number>();
		
	protected callAccesListRefresh = () => this.loadData();
	
	refreshDataSub: Subscription;
	accesListeSub: Subscription;
	
	constructor(
		protected eventBusService: EventBusService,
		protected jsr310Service: JSR310ConversionService, 
		protected accesService: AccesService,
		protected userService: UserService
	) {
		// Si l'evenement de demande de rafraichissement general des donnees est detecte
		this.refreshDataSub = eventBusService.observe(Events.RefreshDataRequested, this.callAccesListRefresh);

		// Si l'evenement de modification de la liste des acces est detecte
		this.accesListeSub = eventBusService.observe(Events.AccesListChanged, this.callAccesListRefresh);
	}
	
	ngOnInit() {
		this.userService.getCurrentUserTLR().subscribe(
			(user: UserTLR) => {
				this.user = user;
				this.callAccesListRefresh();
			}
		);
	}

	ngOnDestroy() {
		if (this.refreshDataSub) {
			this.refreshDataSub.unsubscribe();
		}
		if (this.accesListeSub) {
			this.accesListeSub.unsubscribe();
		}
	}
	
	protected loadData(): void {
		let accesIncomplets$ = this.accesService.findAccesIncomplets(); //AccesIncomplet[]
		let search$ = this.accesService.search(this.searchCriteria);
		combineLatest([accesIncomplets$, search$]).subscribe(
			// on cherche les acces
			([accesIncomplets, acces]: [ AccesIncomplet[], AccesSearchResult[] ]) => {
				let accesPlanifies = acces.filter( $ => $.etat === 'ETAT_ENREGISTRE' );
				let accesEnDemandeEntrees: AccesSearchResult[] = acces.filter( $ => $.etat === 'ETAT_DEMANDE_ENTREE' );
				[accesPlanifies, accesEnDemandeEntrees].forEach( listeAccesPasEncoreValider => {
					listeAccesPasEncoreValider.forEach( accesPasEncoreValider => {
						const accesIncomplet = accesIncomplets.find( $ => $.id === accesPasEncoreValider.id );
						accesPasEncoreValider.entrepriseSeuls = '';
						if (accesIncomplet) {
							accesPasEncoreValider.entrepriseSeuls = accesIncomplet.entreprises;
						}
					} );
				} );
				
				// puis on ventile les acces trouvés dans les differentes piles
				this.reloadAccesPlanifieCommands.next(new AccesSearchResultsWithCriteria(accesPlanifies, this.searchCriteria));
				this.reloadAccesDemandesCommands.next(new AccesSearchResultsWithCriteria(accesEnDemandeEntrees, this.searchCriteria));
				this.reloadAccesEnCoursCommands.next(new AccesSearchResultsWithCriteria(acces.filter( $ => $.etat === 'ETAT_EN_COURS' && $.etatDemandeFermeture !== 0 ), this.searchCriteria));
				this.reloadAccesDemandeFermetureCommands.next(new AccesSearchResultsWithCriteria(acces.filter( $ => $.etat === 'ETAT_EN_COURS' && $.etatDemandeFermeture === 0 ), this.searchCriteria));
				this.reloadAccesTerminesCommands.next(new AccesSearchResultsWithCriteria(acces.filter( $ => $.etat === 'ETAT_TERMINE' ), this.searchCriteria));
				this.reloadAccesRefusesCommands.next(new AccesSearchResultsWithCriteria(acces.filter( $ => $.etat === 'ETAT_REFUSE' ), this.searchCriteria));
				this.reloadAccesEnErreurCommands.next(new AccesSearchResultsWithCriteria(acces.filter( $ => $.etat === 'ETAT_ERREUR'), this.searchCriteria));
			}
		);
	}
	
	onAccesClicked(acces: AccesSearchResult) {
		this.displayAccesDetailsSubject.next(acces.id);
	}
	
	abstract onActionRequest(accesActionRequest: AccesActionRequest<T>): void;
	
	supprimerAcces(accesId: number) {
		this.accesService.supprimerAcces(accesId).pipe(
			catchError(
				(response: HttpErrorResponse) => {
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors de la suppression de cet accès (" + response.status + ")");
					}
					throw new Error();
				}
			)
		).subscribe(
			this.callAccesListRefresh
		);
	}
	
	onAfterFermetureAcces(_acces: AccesTLR, _message: string) {
		this.callAccesListRefresh();
	}
		
	protected persistAccesAutorization(accesId: number, decision: CoactiviteDecision, controleSecurite: number | null): Observable<any> {
	    return this.accesService.changeEtatAcces(accesId, AccesEtat.EN_COURS, controleSecurite).pipe(
	        catchError(
	            (response: HttpErrorResponse) => {
	                if (response.error && response.error.message) {
	                    alert(response.error.message);
	                } else {
	                    alert("Erreur lors de la validation de l'accès #" + accesId + " (" + response.status + ")");
	                }
	                throw new Error();
	            }
	        )
	    );
	}
	
	protected persistAccesDenial(accesId: number, motifId: number): Observable<any> {
		let data = {
			'id': accesId, 
			'motifRefusDto': { id: motifId }, 
			'dateFermeture': this.jsr310Service.toLocalDateTime(new Date()),
		};
		return this.accesService.refuserAcces(data).pipe(
			catchError(
				(response: HttpErrorResponse) => {
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors du refus de cet accès (" + response.status + ")");
					}
					throw new Error();
				}
			)
		);
	}
	
	onCompletingAssociationArrets(_acces: AccesTLR) {
		this.callAccesListRefresh();
	}
	
	onCompletingRefus(acces: AccesTLR[], motifId: number) {
		if (acces.length > 1) {
			throw new Error("L'implementation de base de onCompletingRefus ne fonctionne qu'avec un seul acces");
		}
		
		if (motifId > -1) {
			let accesId = acces[0].id;
			
			this.persistAccesDenial(accesId, motifId).subscribe(
				this.callAccesListRefresh
			);
		}
	}
	
	applySearchCriteria(searchCriteria: AccesSearchCriteria) {
		this.searchCriteria = searchCriteria;
		this.callAccesListRefresh();	
	}
	
	toutExporter() {
		this.exporter(null);
	}
	
	protected adjustCriteriaForExport(etat: AccesEtat | null): AccesSearchCriteria {
		let criteria = { ...this.searchCriteria };
		switch(etat) {
			case AccesEtat.PLANIFIE:
			case AccesEtat.ENREGISTRE:
			case AccesEtat.EN_COURS:
			case AccesEtat.FERME:
			case AccesEtat.REFUSE:
			case AccesEtat.ERREUR:
				criteria.etat = etat;
				break;
			case AccesEtat.ATTENTE_FEMETURE:
				console.log('on veut exporter les acces ATTENTE_FEMETURE');
				criteria.etat = AccesEtat.EN_COURS; // cas particulier de l'etat en cours
				criteria.demandeFermeture = true;
				break;
			default:
		}
		
		return criteria;
	}
	
	protected abstract getExportFileName(etat: AccesEtat | null): string;
	
	protected getExportResult(criteria: AccesSearchCriteria): Observable<Blob> {
		return this.accesService.export(criteria);
	}
		
	protected exporter(etat: AccesEtat | null) {
		// Determiner le veritable criteres de recherche
		let criteria = this.adjustCriteriaForExport(etat);

		// Determiner le nom du fichier telecharge		
		let fileName = this.getExportFileName(etat);
		
		// Lancer la generation et le download du excel en fonction des criteres et de l'etat choisi
		this.getExportResult(criteria).subscribe(
			(blob: Blob) => {
				const objectUrl: string = URL.createObjectURL(blob);
				const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
				a.href = objectUrl;
				a.download = fileName;
				document.body.appendChild(a);
				a.click();
				
				document.body.removeChild(a);
				URL.revokeObjectURL(objectUrl);
  			},
			(err: any) => {
				alert("Erreur lors du téléchargement de l'export");
    			console.log('Could not generate the file, error: ', err);
			}
		);		
	}
	
	notify(acces: AccesTLR) {
		this.eventBusService.emit(Events.NotificationDialogRequested, [acces.id, null]);
	}
}